import { z } from 'zod';
import { ActForOffer, ActingFor } from './acting-for-base.js';
import { LetterWordingText, StageId } from './awards.js';
import { StatementList, StatementRow } from './operations.js';
import { PageInfo, pageFields } from './paging.js';
import { SpotValue } from './spot-value.js';

const Version = z.number().int().positive().max(Number.MAX_SAFE_INTEGER);
const Timestamp = z.iso.datetime({ offset: true });
// APP-232: kept in its own module so Home's contract can read it without an import cycle.
export { SpotValue };
/** Legacy/default range. Managers validate configured or saved policy, not this transport boundary. */
export const SPOT_CASH_RANGE = { minCents: 50000, maxCents: 300000 } as const;
const Cash = z.number().int().positive().max(Number.MAX_SAFE_INTEGER);
export const SpotCashRange = z
  .object({ minCents: Cash, maxCents: Cash })
  .strict()
  .refine((range) => range.maxCents >= range.minCents);
export const SpotApprovalMode = z.enum(['committee_only', 'committee_then_supervisor']);
export const SpotWorkflowConfig = z
  .object({
    committeeIds: z
      .array(z.uuid())
      .max(100)
      .refine((ids) => new Set(ids.map((id) => id.toLowerCase())).size === ids.length),
    approvalMode: SpotApprovalMode,
    cashRange: SpotCashRange,
  })
  .strict();
export type SpotWorkflowConfig = z.infer<typeof SpotWorkflowConfig>;
export const SpotReviewPolicy = z
  .object({
    approvalMode: SpotApprovalMode,
    cashRange: SpotCashRange,
    settingRowId: z.uuid().nullable(),
  })
  .strict();
export type SpotReviewPolicy = z.infer<typeof SpotReviewPolicy>;
export const SpotNominationStatus = z.enum(['draft', 'sent', 'awarded', 'not_awarded']);
/**
 * APP-240 (change 1): the nominator's Title, the line the employee's letter prints ("…a bonus of
 * $1,000 in recognition of {title}."). Short; the server requires it on send. SENT-143: it's letter
 * text, so it's APP-201's `LetterWordingText` (no control characters, NUL included, and no bidi
 * marks), as every other text that prints on a letter, shortened to 80.
 */
export const SPOT_TITLE_MAX = 80;
/** A Title or letter title as typed: blank while drafting. */
export const SpotTitleText = LetterWordingText.max(SPOT_TITLE_MAX);
/** A Title or letter title that must say something: on send, and every letter title edit. */
export const SpotTitle = SpotTitleText.min(1);
/**
 * Nominator identity is never accepted from a client. Incomplete replacements remain drafts.
 *
 * APP-240: `title` (blank while drafting); `description`, the write-up renamed (change 2: stored as
 * `writeup`); the Areté value optional (Q9). `keepSurprise` is retired (Q27: every nomination is a
 * surprise): an older page may still send it, and it's accepted and ignored.
 */
export const SpotDraftFields = z
  .object({
    nomineeIds: z.array(z.uuid()).max(50),
    title: SpotTitleText.nullable().default(null),
    description: z.string().trim().max(10000),
    value: SpotValue.nullable(),
    suggestedAmountCents: Cash.nullable(),
    keepSurprise: z.boolean().optional(),
  })
  .strict();
export type SpotDraftFields = z.infer<typeof SpotDraftFields>;
export const CreateSpotDraft = SpotDraftFields.extend({ commandId: z.uuid() }).strict();
export type CreateSpotDraft = z.infer<typeof CreateSpotDraft>;
export const SaveSpotDraft = SpotDraftFields.extend({ version: Version }).strict();
export type SaveSpotDraft = z.infer<typeof SaveSpotDraft>;
export const SubmitSpotDraft = z.object({ version: Version }).strict();
export type SubmitSpotDraft = z.infer<typeof SubmitSpotDraft>;
export const SpotListQuery = z.object({ ...pageFields }).strict();
export type SpotListQuery = z.input<typeof SpotListQuery>;
export const SpotNominationListQuery = SpotListQuery.extend({
  reviewState: z.enum(['pending', 'completed']).optional(),
}).strict();
export type SpotNominationListQuery = z.input<typeof SpotNominationListQuery>;
/**
 * Supervisor decisions: those waiting on the reader (`pending`, the default), or the ones they
 * decided (`completed`), newest decision first (APP-111), as the committee's queue splits.
 */
export const SpotSupervisionListQuery = SpotListQuery.extend({
  reviewState: z.enum(['pending', 'completed']).optional(),
}).strict();
export type SpotSupervisionListQuery = z.input<typeof SpotSupervisionListQuery>;
export const SpotEntryConfig = z
  .object({
    available: z.boolean(),
    reviewer: z.boolean(),
    supervisorWork: z.boolean().default(false),
    fundingWork: z.boolean().default(false),
    assignmentWork: z.boolean().default(false),
    cashRange: SpotCashRange,
    approvalMode: SpotApprovalMode.default('committee_then_supervisor'),
    committeeConfigured: z.boolean().default(false),
    committeeCount: z.number().int().nonnegative().default(0),
    /**
     * APP-226: the All Spot awards view is theirs to open: the committee, the Stock Admin team,
     * System Admins, and the manager of anyone nominated.
     */
    allWork: z.boolean().default(false),
    today: z.iso.date().optional(),
  })
  .strict();
export type SpotEntryConfig = z.infer<typeof SpotEntryConfig>;
const Person = z.object({ id: z.uuid(), name: z.string() }).strict();
/** APP-268: one row of the committee list. `active`: they can still review, as review itself tests. */
const SpotCommitteeRow = Person.extend({
  jobTitle: z.string().nullable(),
  sector: z.string().nullable(),
  active: z.boolean(),
}).strict();
export type SpotCommitteeRow = z.infer<typeof SpotCommitteeRow>;
export const SpotWorkflowSetup = z
  .object({
    /** Who reviews Spot nominations today: the committee, or the Executives until it's named. */
    members: z.array(Person),
    /** APP-220: everyone the stored committee names or has named, so the page and its History read names. */
    named: z.array(Person),
    /** APP-268: today's reviewers and the saved committee, for the list's rows. Never older members. */
    people: z.array(SpotCommitteeRow).default([]),
    /** APP-220: sent nominations waiting for the committee while nobody can decide them, else 0. */
    waiting: z.number().int().nonnegative(),
    /**
     * APP-222: this person can return Spot Award setup to not set now: a System Admin or the Stock
     * Admin team, outside Preview, while a setup is saved.
     */
    canReset: z.boolean().default(false),
  })
  .strict();
export type SpotWorkflowSetup = z.infer<typeof SpotWorkflowSetup>;
/**
 * APP-222: what returning Spot Award setup to not set would change, for its dialog. Counts only,
 * besides the names of who reviews before and after, which the setup read already gives these
 * readers. APP-254: no count of letters to make, since the Stock Admin team checks and releases
 * Spot letters whoever reviews (APP-244); only a letter losing its holder changes.
 */
export const SpotWorkflowReset = z
  .object({
    /** The stored setup these figures were made from, sent back as the reset's `basedOn`. */
    version: z.uuid(),
    /** Who reviews Spot nominations today. */
    reviewers: z.array(Person),
    /** Who reviews once it's reset: the rule before any setup was saved. */
    reviewersAfter: z.array(Person),
    /**
     * Whether that rule is every active Executive, new ones joining automatically. False only while
     * the older `spot.reviewers` list names people, who then review in the Executives' place, as
     * they did before any setup.
     */
    executivesReview: z.boolean(),
    /** Sent nominations with someone waiting for the committee: they pass to `reviewersAfter`. */
    waiting: z.number().int().nonnegative(),
    /** Spot awards whose earning statement is waiting to be checked. */
    statements: z.number().int().nonnegative(),
    /**
     * Letters not yet handed over whose holder stops being able to hold them: a committee member
     * who doesn't review once it's reset and isn't the colleague's manager. The Stock Admin team
     * reroutes each to a new holder (APP-244).
     */
    lettersLosingHolder: z.number().int().nonnegative(),
  })
  .strict();
export type SpotWorkflowReset = z.infer<typeof SpotWorkflowReset>;
/** APP-222: the reset itself. Always in the person's own name: there is no `actingFor`. */
export const ResetSpotWorkflow = z
  .object({
    /** The stored setup the dialog was made from (`SpotWorkflowReset.version`). */
    basedOn: z.uuid(),
    reason: z.string().trim().min(1).max(2000),
  })
  .strict();
export type ResetSpotWorkflow = z.infer<typeof ResetSpotWorkflow>;
export const SpotPerson = Person.extend({
  sector: z.object({ id: z.string(), name: z.string() }).strict().nullable(),
  eligible: z.boolean(),
}).strict();
export type SpotPerson = z.infer<typeof SpotPerson>;
const Fingerprint = z.string().regex(/^[a-f0-9]{64}$/);
export const SpotFundingQuote = z
  .object({
    fingerprint: Fingerprint,
    pool: z.object({ id: z.string(), name: z.string() }).strict(),
    fiscalYear: z.number().int(),
    cashCents: Cash,
    coding: z
      .object({
        costType: z.enum(['OH', 'GA']),
        account: z.string(),
        project: z.null(),
        org: z.string(),
      })
      .strict(),
    budgetStatus: z.enum(['not_set', 'ok', 'over_budget']),
    shortfallCents: z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER),
    blockers: z.array(z.string()),
  })
  .strict();
export type SpotFundingQuote = z.infer<typeof SpotFundingQuote>;
/** `cancelled` (APP-226): cancelled before the letter was released, not a decision. */
export const SpotReviewStage = z.enum([
  'committee_pending',
  'supervisor_pending',
  'awarded',
  'not_awarded',
  'cancelled',
]);
/**
 * APP-227: how the manager said yes or no outside MyRewards, when someone records it for them. The
 * words are the web's ("by email", "on Teams", "by phone", "in person", "another way").
 */
export const SpotRecordedHow = z.enum(['email', 'teams', 'phone', 'in_person', 'other']);
export type SpotRecordedHow = z.infer<typeof SpotRecordedHow>;
/**
 * APP-227: a manager's decision recorded for them: whose decision it is and what it was, who
 * recorded it, how the manager told them, and the note.
 */
export const SpotRecordedDecision = z
  .object({
    supervisor: Person,
    decision: z.enum(['approved', 'vetoed']),
    by: Person,
    how: SpotRecordedHow,
    note: z.string(),
  })
  .strict();
export type SpotRecordedDecision = z.infer<typeof SpotRecordedDecision>;
/** SENT-129: who appointed a colleague's supervisor (no manager to decide), when and why. */
export const SpotAppointment = z.object({ by: Person, at: Timestamp, reason: z.string() }).strict();
export const SpotReviewOutcome = z
  .object({
    personId: z.uuid(),
    version: Version,
    stage: SpotReviewStage,
    approvalAuthority: z.enum(['committee', 'supervisor']).nullable().optional(),
    committeeById: z.uuid().nullable(),
    committeeAt: Timestamp.nullable(),
    recommendedCashCents: Cash.nullable(),
    awardDate: z.iso.date().nullable(),
    /** APP-241 (change 10): the committee's notes, for each colleague's manager; null if none. */
    reviewSummary: z.string().nullable(),
    /** Retired by APP-241: always false, never offered. */
    publishCitation: z.boolean(),
    committeeNote: z.string().nullable(),
    /**
     * APP-241 (Q26): this colleague's letter title, first the nominator's Title, which the
     * committee edits until it decides on them. Null on a nomination sent before titles.
     */
    letterTitle: z.string().nullable().optional(),
    effectiveSupervisor: Person.nullable(),
    /**
     * APP-243 (change 13, Q25 = A): always null. The committee reads no pool, budget or funding;
     * kept so an older page still parses.
     */
    quote: SpotFundingQuote.nullable(),
    /** What stops the colleague's award being made: setup, or no supervisor. Never the budget. */
    blockers: z.array(z.string()),
    supervisorNote: z.string().nullable(),
    supervisorById: z.uuid().nullable(),
    awardRequestId: z.uuid().nullable(),
    awardLineId: z.uuid().nullable(),
    /** APP-226: who cancelled this colleague before the letter was released, when and why. */
    cancelled: z
      .object({ by: Person, at: Timestamp, reason: z.string() })
      .strict()
      .nullable()
      .optional(),
    /** APP-227: the supervisor's decision was recorded for them, by whom and how. */
    recorded: SpotRecordedDecision.nullable().optional(),
    /** SENT-129: who appointed the supervisor shown, when and why. */
    appointed: SpotAppointment.nullable().optional(),
  })
  .strict();
export type SpotReviewOutcome = z.infer<typeof SpotReviewOutcome>;
const SpotDetailBase = z
  .object({
    id: z.uuid(),
    version: Version,
    status: SpotNominationStatus,
    createdAt: Timestamp,
    updatedAt: Timestamp,
    submittedAt: Timestamp.nullable(),
    view: z.enum(['author', 'reviewer']),
    nominator: Person,
    nominees: z.array(SpotPerson),
    /** APP-240: the Title, the letter's line; null on a draft without one or an older nomination. */
    title: z.string().nullable(),
    /** APP-240: what they did, for the committee and the manager. Never on the letter. */
    description: z.string(),
    value: SpotValue.nullable(),
    suggestedAmountCents: Cash.nullable(),
    reviewPolicy: SpotReviewPolicy.optional(),
    reviewAvailable: z.boolean().optional(),
    actions: z.object({ edit: z.boolean(), submit: z.boolean() }).strict(),
  })
  .strict();
/**
 * APP-225 (Tom: the nominator "just needs to get acknowledgement"): the author's own copy says only
 * that it's a draft or that it was sent for review. It carries no per-nominee stage or outcome, and
 * its `updatedAt` stays at the time it was sent, so a decision moves nothing the author can see.
 * A draft keeps its `version`, which the author's next save or send must match. A sent copy has
 * none: the author can't change it, and the nomination's version moves with every committee and
 * supervisor step, which would tell the author that something happened.
 */
export const SpotAuthorDraft = SpotDetailBase.extend({
  view: z.literal('author'),
  status: z.literal('draft'),
}).strict();
export const SpotAuthorSent = SpotDetailBase.omit({ version: true })
  .extend({ view: z.literal('author'), status: z.literal('sent') })
  .strict();
export const SpotAuthorDetail = z.discriminatedUnion('status', [SpotAuthorDraft, SpotAuthorSent]);
export const SpotCommitteeDetail = SpotDetailBase.extend({
  view: z.literal('reviewer'),
  /** Review notes written before APP-241 (no longer written): kept for the committee to read. */
  notes: z
    .array(z.object({ id: z.uuid(), by: Person, note: z.string(), createdAt: Timestamp }).strict())
    .optional(),
  outcomes: z.array(SpotReviewOutcome).optional(),
}).strict();
export const SpotNominationDetail = z.discriminatedUnion('view', [
  SpotAuthorDetail,
  SpotCommitteeDetail,
]);
export type SpotNominationDetail = z.infer<typeof SpotNominationDetail>;
/** Acknowledgment does not authorize the full detail endpoint. */
export const SpotReceivedAcknowledgment = z
  .object({
    id: z.uuid(),
    /** APP-240: null when the nominator chose no Areté value (Q9). */
    value: SpotValue.nullable(),
    submittedAt: Timestamp,
    status: z.enum(['sent', 'awarded', 'not_awarded']),
  })
  .strict();
export type SpotReceivedAcknowledgment = z.infer<typeof SpotReceivedAcknowledgment>;
const page = {
  count: z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER),
  page: PageInfo,
  config: SpotEntryConfig,
};
export const SpotNominationPage = z
  .object({ ...page, items: z.array(SpotNominationDetail) })
  .strict();
export type SpotNominationPage = z.infer<typeof SpotNominationPage>;
export const SpotReceivedPage = z
  .object({ ...page, items: z.array(SpotReceivedAcknowledgment) })
  .strict();
export type SpotReceivedPage = z.infer<typeof SpotReceivedPage>;
export const SpotPeopleQuery = z
  .object({
    q: z.string().trim().min(1).max(100),
    limit: z.coerce.number().int().min(1).max(50).default(20),
  })
  .strict();
export const SpotPeoplePage = z.object({ items: z.array(SpotPerson) }).strict();

const Reason = z.string().trim().min(1).max(2000);
const command = { commandId: z.uuid(), version: Version };
const ProposedSpotApproval = z
  .object({ personId: z.uuid(), cashCents: Cash, awardDate: z.iso.date() })
  .strict();
export const PreviewSpotCommittee = z
  .object({ version: Version, approvals: z.array(ProposedSpotApproval).min(1).max(50) })
  .strict()
  .refine(
    (v) => new Set(v.approvals.map((a) => a.personId.toLowerCase())).size === v.approvals.length,
  );
export type PreviewSpotCommittee = z.infer<typeof PreviewSpotCommittee>;
/**
 * APP-243 (change 13, Q25 = A): the facts a committee-only approval is made from, checked before
 * it's confirmed. Never the pool, its budget or funding: a short pool never holds the award, and
 * its top-up goes to the Executives on its own. `factsFingerprint` is sent back with the approval
 * (`CommitteeDecision.factsFingerprint`); null, with `blockers`, while the award can't be made.
 */
export const SpotCommitteePreview = z
  .object({
    version: Version,
    items: z.array(
      z
        .object({
          personId: z.uuid(),
          factsFingerprint: Fingerprint.nullable(),
          blockers: z.array(z.string()),
        })
        .strict(),
    ),
    blockers: z.array(z.string()),
  })
  .strict();
export type SpotCommitteePreview = z.infer<typeof SpotCommitteePreview>;
/**
 * APP-241: the committee's decision for each colleague it decides. `letterTitle` saves an edit to
 * that colleague's letter title with the decision (Q26). On a recommendation, `summary` and
 * `publishCitation` are retired (change 10): an older page may still send them; a summary is kept
 * as that colleague's notes when the command has no `notes`, and nothing is ever published.
 */
const CommitteeDecision = z.discriminatedUnion('decision', [
  ProposedSpotApproval.extend({
    decision: z.literal('approve'),
    /**
     * APP-243: `SpotCommitteePreview` item's `factsFingerprint`: what the award is made from, but
     * the pool's budget position, checked again at the decision (change 14).
     */
    factsFingerprint: Fingerprint,
    note: Reason.optional(),
    letterTitle: SpotTitle.optional(),
  }).strict(),
  z
    .object({
      personId: z.uuid(),
      decision: z.literal('recommend'),
      cashCents: Cash,
      awardDate: z.iso.date(),
      summary: Reason.optional(),
      publishCitation: z.boolean().optional(),
      note: Reason.optional(),
      letterTitle: SpotTitle.optional(),
    })
    .strict(),
  z.object({ personId: z.uuid(), decision: z.literal('decline'), note: Reason }).strict(),
]);
/**
 * APP-241 (change 10): `notes`, "Notes from the committee", one optional box for the whole
 * nomination, which each colleague's manager reads with the title and description, so the same
 * words reach every colleague's manager (O3). Saved with each colleague recommended or approved in
 * this command; the employee never sees them.
 */
export const DecideSpotCommittee = z
  .object({
    ...command,
    decisions: z.array(CommitteeDecision).min(1).max(50),
    notes: Reason.optional(),
  })
  .strict()
  .refine(
    (v) => new Set(v.decisions.map((d) => d.personId.toLowerCase())).size === v.decisions.length,
    'Name each colleague once.',
  );
export type DecideSpotCommittee = z.input<typeof DecideSpotCommittee>;
/**
 * APP-241 (Q26): the committee edits a colleague's letter title until it decides on them. The
 * `version` is the nomination's, as for a committee decision. The reason is optional: the edit is
 * recorded with who made it, before and after, and the reason when one is given. APP-243: the
 * manager edits it with the same command until they approve, the `version` then the colleague's
 * (`SpotSupervisionItem.version`), as for their decision.
 */
export const EditSpotLetterTitle = z
  .object({ ...command, letterTitle: SpotTitle, reason: Reason.optional() })
  .strict();
export type EditSpotLetterTitle = z.infer<typeof EditSpotLetterTitle>;
/**
 * APP-227 (Tom: "someone else needs to see them in the queue and be able to approve them and just
 * take note that they got approval"): present when a committee member or a Stock Admin records the
 * manager's decision for them. A note is required; a veto still gives the manager's reason.
 */
const RecordFor = z.object({ how: SpotRecordedHow, note: Reason }).strict();
/**
 * APP-243 (change 15): how the letter reaches the employee, chosen with the approval: the manager
 * hands it out (`hand_out`), or it's released to the employee once the Stock Admin team has
 * checked it (`automatic`). Fixed once chosen.
 */
export const SpotReleasePath = z.enum(['hand_out', 'automatic']);
export type SpotReleasePath = z.infer<typeof SpotReleasePath>;
export const DecideSpotSupervisor = z.discriminatedUnion('decision', [
  z
    .object({
      ...command,
      decision: z.literal('approve'),
      /**
       * APP-243: `SpotSupervisionItem.factsFingerprint` as it was read. Everything the award is
       * made from but the pool's budget, and whether the employee's email goes, checked again at
       * the decision (change 14: no Refresh button, the server checks).
       */
      factsFingerprint: Fingerprint,
      releasePath: SpotReleasePath,
      /**
       * APP-243 (S4-ManagerDecision): an optional note for the record, kept with the decision and
       * in the award's History. A veto keeps its required reason instead.
       */
      note: Reason.optional(),
      recorded: RecordFor.optional(),
    })
    .strict(),
  z
    .object({
      ...command,
      decision: z.literal('veto'),
      reason: Reason,
      recorded: RecordFor.optional(),
    })
    .strict(),
]);
export type DecideSpotSupervisor = z.infer<typeof DecideSpotSupervisor>;
export const AssignSpotSupervisor = z
  .object({ ...command, supervisorId: z.uuid(), reason: Reason })
  .strict();
export type AssignSpotSupervisor = z.infer<typeof AssignSpotSupervisor>;
export const RequestSpotFunding = z
  .object({
    ...command,
    quoteFingerprint: Fingerprint,
    proposal: z.object({ cashCents: Cash, awardDate: z.iso.date() }).strict().optional(),
  })
  .strict();
export type RequestSpotFunding = z.infer<typeof RequestSpotFunding>;
export const DecideSpotFunding = z.discriminatedUnion('decision', [
  z
    .object({
      ...command,
      decision: z.literal('approve'),
      topUpCents: z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER).optional(),
      reason: Reason,
      actingFor: ActingFor.optional(),
    })
    .strict(),
  z
    .object({
      ...command,
      decision: z.literal('decline'),
      reason: Reason,
      actingFor: ActingFor.optional(),
    })
    .strict(),
]);
export type DecideSpotFunding = z.infer<typeof DecideSpotFunding>;
/**
 * The manager's decision on one colleague (APP-243, changes 12 to 15). What was nominated leads:
 * the letter title, the nominator's description and the committee's notes. Never the pool, its
 * budget or funding (change 13): the award goes ahead when the pool is short, and the top-up goes
 * to the Executives on its own (Q25 = A).
 */
export const SpotSupervisionItem = z
  .object({
    nominationId: z.uuid(),
    person: Person,
    version: Version,
    stage: SpotReviewStage,
    /** APP-240: null when the nominator chose no Areté value (Q9). */
    value: SpotValue.nullable(),
    /**
     * APP-241: the committee's notes; null when it left none (Q12, Q13). One note for the whole
     * nomination: every colleague's manager reads the same words, which may mention the others.
     * APP-243: the manager reads them as "Notes from the committee", with the title and description.
     */
    summary: z.string().nullable(),
    /** APP-243: this colleague's letter title, the line the letter prints; null on an older one. */
    title: z.string().nullable(),
    /**
     * APP-243 (S4-ManagerDecision): the other colleagues on this nomination who report to this
     * manager and wait on their decision, each with their own row in Waiting on you. Never a
     * colleague who reports to someone else, and none for whoever records the decision.
     */
    alsoYours: z.array(Person).optional(),
    /**
     * APP-243: the nominator's description (Q16, reversing APP-226 D3 for this screen). For the
     * supervisor deciding, and a committee member recording it for them. Null for a Stock Admin
     * recording it, who doesn't read what the nominator wrote (APP-108).
     */
    description: z.string().nullable(),
    cashCents: Cash,
    awardDate: z.iso.date(),
    effectiveSupervisor: Person.nullable(),
    /**
     * Sent back with an approval (`DecideSpotSupervisor.factsFingerprint`); null while the award
     * can't be made. The server checks it again at the decision.
     */
    factsFingerprint: Fingerprint.nullable(),
    /**
     * D3: whether this colleague's "Your award is ready" email reaches them now. When it doesn't,
     * the automatic release says they'll only see it in MyRewards.
     */
    emailReaches: z.boolean(),
    /** Once approved, how the letter reaches the employee; null before then. */
    releasePath: SpotReleasePath.nullable(),
    /** Why it can't be approved yet, in words that never name a pool or budget. */
    blockers: z.array(z.string()),
    actions: z.object({ approve: z.boolean(), veto: z.boolean(), editTitle: z.boolean() }).strict(),
    /**
     * APP-227: the manager whose decision this reader records for them (a committee member or a
     * Stock Admin), or null when the reader is the supervisor deciding for themselves.
     */
    recording: Person.nullable().optional(),
    /** APP-227: once decided, the decision was recorded for the supervisor, by whom and how. */
    recorded: SpotRecordedDecision.nullable().optional(),
    /** SENT-129: who appointed this supervisor, when and why. */
    appointed: SpotAppointment.nullable().optional(),
  })
  .strict();
export type SpotSupervisionItem = z.infer<typeof SpotSupervisionItem>;
export const SpotSupervisionPage = z
  .object({
    items: z.array(SpotSupervisionItem),
    count: z.number().int().nonnegative(),
    page: PageInfo,
  })
  .strict();
const SpotFundingActions = z.object({ approve: z.boolean(), decline: z.boolean() }).strict();
export const SpotFundingItem = z
  .object({
    id: z.uuid(),
    /**
     * The colleague an asked request is about. Null on an automatic one (SENT-158, APP-243 D4):
     * that one is its pool's and year's, never the colleague's.
     */
    nominationId: z.uuid().nullable(),
    personId: z.uuid().nullable(),
    version: Version,
    /**
     * An Executive's answer, or `pending` while it waits on one. SENT-132: `not_needed` when it
     * was never answered and its colleague no longer waits on the decision it was asked for
     * (cancelled, vetoed, declined, or approved once the pool had room); stored as `pending`.
     * APP-264: `closed` when a system rule closed it unanswered (Tom, 2026-11-01: Spot awards
     * aren't held for budget, and the Executives aren't asked for a top-up).
     */
    state: z.enum(['pending', 'approved', 'declined', 'not_needed', 'closed']),
    /**
     * APP-243: who opened it. `asked`: someone sent the shortfall before a decision. `automatic`
     * (Q25 = A): MyRewards did, when an approved award left the pool short. An automatic one is
     * about its pool and year: it waits while the pool is short (`not_needed` once it isn't).
     */
    origin: z.enum(['asked', 'automatic']),
    /**
     * APP-243: the cash that brings the pool's year back to zero now, the top-up's default (the
     * Budget pools rule). Null while the pool can't be read.
     */
    shortfallCents: z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER).nullable(),
    poolId: z.string(),
    fiscalYear: z.number().int(),
    requestedCashCents: Cash,
    requestedAt: Timestamp,
    person: Person.nullable(),
    poolName: z.string().nullable(),
    quote: SpotFundingQuote.nullable(),
    blockers: z.array(z.string()),
    actions: SpotFundingActions,
    /** APP-148: for a Stock Admin, the Executives they may decide it for, and what each may do. */
    actingFor: ActForOffer.extend({ actions: SpotFundingActions }).strict().nullable().optional(),
  })
  .strict();
export type SpotFundingItem = z.infer<typeof SpotFundingItem>;
export const SpotFundingPage = z
  .object({
    items: z.array(SpotFundingItem),
    count: z.number().int().nonnegative(),
    page: PageInfo,
  })
  .strict();

/**
 * Operational projections deliberately omit the private nomination and sibling identities as
 * fields. Free text is the exception (O3): the supervisor's item carries the committee's notes,
 * one note for the whole nomination that every colleague's manager reads
 * (`SpotSupervisionItem.summary`).
 */
export const SpotFulfillmentItem = z
  .object({
    requestId: z.uuid(),
    lineId: z.uuid(),
    person: Person,
    displayId: z.string().nullable(),
    state: StageId,
    requestVersion: Version,
    lineUpdatedAt: Timestamp,
    cashCents: z.number().int().nonnegative(),
    awardDate: z.iso.date(),
    publicCitation: z.string().nullable().optional(),
    citationRequired: z.boolean().optional(),
    holder: Person.extend({ status: z.enum(['active', 'left']) })
      .strict()
      .nullable(),
    /** APP-244: the Stock Admin holding the letters step's claim, if anyone. */
    claimedBy: Person.nullable().optional(),
    /**
     * APP-244: how the letter reaches the colleague once released: handed out by their manager,
     * or sent to them straight away with a copy to the manager (APP-245 delivers it). The manager
     * is the letter's holder either way.
     */
    releasePath: z.enum(['hand_out', 'automatic']).optional(),
    /**
     * APP-247: the colleague's manager chose the release path when approving (APP-243). False when
     * the committee decided alone, or for an award approved before the choice existed: the letter
     * holder hands it out. Optional: older servers omit it.
     */
    releasePathChosen: z.boolean().optional(),
    /**
     * APP-247: when the letter was released, and by whom (null when no person did), once it is.
     * Optional: older servers omit it.
     */
    released: z.object({ at: Timestamp, by: Person.nullable() }).strict().nullable().optional(),
    actions: z
      .object({
        generate: z.boolean(),
        release: z.boolean(),
        reroute: z.boolean(),
        /** APP-244: a Stock Admin may claim the letters step now (nobody holds it). */
        claim: z.boolean().optional(),
      })
      .strict(),
    blockers: z.array(z.string()),
  })
  .strict();
export type SpotFulfillmentItem = z.infer<typeof SpotFulfillmentItem>;
const workPage = { count: z.number().int().nonnegative(), page: PageInfo };
export const SpotFulfillmentPage = z
  .object({ ...workPage, items: z.array(SpotFulfillmentItem) })
  .strict();
export type SpotFulfillmentPage = z.infer<typeof SpotFulfillmentPage>;
export const SpotAssignmentItem = z
  .object({
    nominationId: z.uuid(),
    person: Person,
    version: Version,
    effectiveSupervisor: Person.nullable(),
    blockers: z.array(z.string()),
    actions: z.object({ assign: z.boolean() }).strict(),
  })
  .strict();
export type SpotAssignmentItem = z.infer<typeof SpotAssignmentItem>;
export const SpotCandidatePage = z.object({ items: z.array(Person) }).strict();
export type SpotCandidatePage = z.infer<typeof SpotCandidatePage>;
export const SpotStatementRow = StatementRow.extend({
  canDecide: z.boolean(),
  blockedReason: z.string().nullable(),
});
export type SpotStatementRow = z.infer<typeof SpotStatementRow>;
export const SpotStatementList = StatementList.extend({ rows: z.array(SpotStatementRow) });
export type SpotStatementList = z.infer<typeof SpotStatementList>;

/**
 * APP-226 (Tom: "the stock administrators still need to see a view of the spot awards that are in
 * the system"; "The committee members also need to see the queue, same exact queue"; "Managers
 * should see the queue"): All Spot awards, one row per nominee on a sent nomination. It never
 * carries the nominator or the write-up: the committee reads those by opening the nomination.
 * The stages and who it waits on mirror `@myrewards/domain`'s `SPOT_QUEUE_STAGES` and
 * `SPOT_QUEUE_WAITING` (a server test keeps them equal). APP-233: `funding`, a decision held while
 * the Executives decide a funding shortfall.
 */
export const SpotQueueStage = z.enum([
  'committee',
  'funding',
  'manager',
  'no_manager',
  'letter',
  'released',
  'payroll',
  'paid',
  'closed',
  'not_awarded',
  'cancelled',
]);
export type SpotQueueStage = z.infer<typeof SpotQueueStage>;
/** APP-247: `stock_admin`, the Stock Admin team checking and releasing the letter (change 17). */
export const SpotQueueWaiting = z.enum([
  'committee',
  'manager',
  'no_manager',
  'executives',
  'stock_admin',
  'holder',
  'payroll',
  'none',
]);
export type SpotQueueWaiting = z.infer<typeof SpotQueueWaiting>;
/**
 * APP-233: the stage filter. `in_progress`, the default, keeps every stage still in progress (Home's
 * card's rule); `all` keeps every stage; a stage keeps that stage. Mirrors the domain's
 * `SPOT_QUEUE_STAGE_FILTERS`.
 */
export const SpotQueueStageFilter = z.enum([
  'in_progress',
  'all',
  'committee',
  'funding',
  'manager',
  'no_manager',
  'letter',
  'released',
  'payroll',
  'paid',
  'closed',
  'not_awarded',
  'cancelled',
]);
export type SpotQueueStageFilter = z.infer<typeof SpotQueueStageFilter>;
/**
 * APP-233 (APP-149 D3): the columns All Spot awards sorts by. The server sorts the whole list, then
 * pages it (APP-149 B3: a list paged on the server sorts there). Oldest sent first by default.
 */
export const SpotQueueSort = z.enum(['colleague', 'stage', 'value', 'sent', 'amount', 'award']);
export type SpotQueueSort = z.infer<typeof SpotQueueSort>;
export const SpotQueueQuery = SpotListQuery.extend({
  stage: SpotQueueStageFilter.default('in_progress'),
  waiting: SpotQueueWaiting.optional(),
  sort: SpotQueueSort.default('sent'),
  dir: z.enum(['asc', 'desc']).default('asc'),
}).strict();
export type SpotQueueQuery = z.input<typeof SpotQueueQuery>;
export const SpotQueueRow = z
  .object({
    nominationId: z.uuid(),
    /** The nominee's own version: a cancel names the copy it was made from. */
    version: Version,
    nominee: Person,
    /** APP-240: null when the nominator chose no Areté value (Q9). */
    value: SpotValue.nullable(),
    sentAt: Timestamp,
    stage: SpotQueueStage,
    /**
     * The person when it waits on one person (the manager, the letter holder, or APP-247 the Stock
     * Admin holding the letter step's claim), else null.
     */
    waitingOn: z.object({ kind: SpotQueueWaiting, person: Person.nullable() }).strict(),
    cashCents: Cash.nullable(),
    awardDate: z.iso.date().nullable(),
    holder: Person.nullable(),
    awardRequestId: z.uuid().nullable(),
    displayId: z.string().nullable(),
    cancelled: z.object({ by: Person, at: Timestamp }).strict().nullable(),
    /** Where this reader acts on it; each is a link but `cancel`. */
    actions: z
      .object({
        /** The committee's review of the nomination. */
        review: z.boolean(),
        /** The reader's own decision as the nominee's supervisor. */
        decide: z.boolean(),
        /** APP-227: record the manager's approval or veto for them. */
        record: z.boolean(),
        /** APP-227: appoint a supervisor when no manager is set. */
        appoint: z.boolean(),
        /**
         * APP-247 (change 17): the Stock Admin team's letter check, at the Letter stage. Before,
         * the committee's letter step.
         */
        letter: z.boolean(),
        /**
         * APP-247: the committee reads the letter there, as it always could, without working it.
         * Optional: older servers omit it.
         */
        readLetter: z.boolean().optional(),
        /** The award's record (G1). */
        award: z.boolean(),
        cancel: z.boolean(),
      })
      .strict(),
    /** Why this reader, who could otherwise cancel it, can't now. */
    cancelBlocked: z.string().nullable(),
  })
  .strict();
export type SpotQueueRow = z.infer<typeof SpotQueueRow>;
const Count = z.number().int().nonnegative();
/**
 * APP-233 (APP-149 D4, B11): what each filter choice would show with the other filter as it is, so
 * a count in the filter's menu is always what choosing it shows.
 */
export const SpotQueueCounts = z
  .object({
    stage: z.record(SpotQueueStageFilter, Count),
    waiting: z.record(SpotQueueWaiting, Count),
  })
  .strict();
export type SpotQueueCounts = z.infer<typeof SpotQueueCounts>;
/**
 * A page of All Spot awards: `count` is how many match the filters, `total` (APP-233) how many the
 * reader may follow in all, for "Showing 9 of 172".
 */
export const SpotQueuePage = z
  .object({ ...workPage, items: z.array(SpotQueueRow), total: Count, counts: SpotQueueCounts })
  .strict();
export type SpotQueuePage = z.infer<typeof SpotQueuePage>;
/**
 * APP-232: the stages of All Spot awards still in progress, in order: everything but Closed, Not
 * awarded and Cancelled. Mirrors `@myrewards/domain`'s `SPOT_IN_PROGRESS_STAGES`.
 */
export const SpotInProgressStage = SpotQueueStage.extract([
  'committee',
  'funding',
  'manager',
  'no_manager',
  'letter',
  'released',
  'payroll',
  'paid',
]);
export type SpotInProgressStage = z.infer<typeof SpotInProgressStage>;
/**
 * APP-232 (Tom: Stock Admins "need to see Spot awards and where each one is at"): Home's "Spot
 * awards in progress", for whoever reads every row of All Spot awards. How many colleagues
 * nominated are at each stage still in progress, every such stage in order, by the tab's own
 * stage rule. Counts only: no nominee, nominator, write-up or amount.
 */
export const SpotInProgress = z
  .object({
    stages: z.array(
      z.object({ stage: SpotInProgressStage, count: z.number().int().nonnegative() }).strict(),
    ),
    total: z.number().int().nonnegative(),
  })
  .strict();
export type SpotInProgress = z.infer<typeof SpotInProgress>;
/** APP-226: cancel a nominee before the letter is released. A reason is required. */
export const CancelSpotNominee = z.object({ ...command, reason: Reason }).strict();
export type CancelSpotNominee = z.infer<typeof CancelSpotNominee>;

/**
 * APP-241 (change 6): the committee queue, one row per sent nomination, in place of the "Review
 * nominations" cards. A nomination waits on the committee while any colleague on it does; part
 * decided while some do and some don't; decided once none does.
 */
export const SpotCommitteeStage = z.enum(['waiting', 'part_decided', 'decided']);
export type SpotCommitteeStage = z.infer<typeof SpotCommitteeStage>;
/** `waiting`, the default, keeps every nomination with a colleague still waiting (part decided too). */
export const SpotCommitteeStageFilter = z.enum(['waiting', 'decided', 'all']);
export type SpotCommitteeStageFilter = z.infer<typeof SpotCommitteeStageFilter>;
/** An Areté value, or `none` for a nomination sent without one (Q9). */
export const SpotCommitteeValueFilter = z.enum(['any', ...SpotValue.options, 'none']);
export type SpotCommitteeValueFilter = z.infer<typeof SpotCommitteeValueFilter>;
/** When it was sent, as the Stock Admin queue's Submitted filter: the last 7 or 30 days, or this year. */
export const SpotCommitteeSentFilter = z.enum(['any', 'week', 'month', 'year']);
export type SpotCommitteeSentFilter = z.infer<typeof SpotCommitteeSentFilter>;
/** The columns the queue sorts by, on the server over the whole list; oldest sent first by default. */
export const SpotCommitteeSort = z.enum([
  'title',
  'nominator',
  'value',
  'suggested',
  'sent',
  'stage',
]);
export type SpotCommitteeSort = z.infer<typeof SpotCommitteeSort>;
export const SpotCommitteeQueueQuery = SpotListQuery.extend({
  stage: SpotCommitteeStageFilter.default('waiting'),
  value: SpotCommitteeValueFilter.default('any'),
  sent: SpotCommitteeSentFilter.default('any'),
  sort: SpotCommitteeSort.default('sent'),
  dir: z.enum(['asc', 'desc']).default('asc'),
}).strict();
export type SpotCommitteeQueueQuery = z.input<typeof SpotCommitteeQueueQuery>;
export const SpotCommitteeQueueRow = z
  .object({
    nominationId: z.uuid(),
    /** The nominator's Title; null on a nomination sent before titles. */
    title: z.string().nullable(),
    /** Every colleague on it, in the order the nominator named them. */
    nominees: z.array(Person),
    nominator: Person,
    value: SpotValue.nullable(),
    suggestedAmountCents: Cash.nullable(),
    sentAt: Timestamp,
    stage: SpotCommitteeStage,
    /** How many of its colleagues no longer wait on the committee ("1 of 3 decided"). */
    decided: Count,
  })
  .strict();
export type SpotCommitteeQueueRow = z.infer<typeof SpotCommitteeQueueRow>;
/** Each filter choice's count with the other filters as they are (APP-149 B11). */
export const SpotCommitteeQueueCounts = z
  .object({
    stage: z.record(SpotCommitteeStageFilter, Count),
    value: z.record(SpotCommitteeValueFilter, Count),
    sent: z.record(SpotCommitteeSentFilter, Count),
  })
  .strict();
export type SpotCommitteeQueueCounts = z.infer<typeof SpotCommitteeQueueCounts>;
/**
 * A page of the committee queue: `count` matches the filters, `total` is every sent nomination.
 * `today` is the organization's date the ages count to.
 */
export const SpotCommitteeQueuePage = z
  .object({
    ...workPage,
    items: z.array(SpotCommitteeQueueRow),
    total: Count,
    counts: SpotCommitteeQueueCounts,
    today: z.iso.date(),
  })
  .strict();
export type SpotCommitteeQueuePage = z.infer<typeof SpotCommitteeQueuePage>;

/**
 * APP-241 (change 8, Q14): each colleague's awards in the last 12 months, for the committee's
 * review: how many, the total, and the list (award, date, amount). Only awards the employee can
 * already see in My rewards, plus Spot awards made (the committee already sees every Spot award):
 * never another award still pending or unreleased.
 */
export const SpotHistoryAward = z
  .object({
    /** The award ID ("PAA-2026-0011"); null for an award loaded without one. */
    displayId: z.string().nullable(),
    type: z.enum(['SDB', 'EDB', 'PAA', 'TAA', 'SPOT']),
    awardDate: z.iso.date(),
    /** The award's value for this person: cash plus any options at their saved value. */
    amountCents: z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER),
  })
  .strict();
export type SpotHistoryAward = z.infer<typeof SpotHistoryAward>;
export const SpotColleagueHistory = z
  .object({
    personId: z.uuid(),
    count: Count,
    totalCents: z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER),
    /** Newest award date first. */
    awards: z.array(SpotHistoryAward),
  })
  .strict();
export type SpotColleagueHistory = z.infer<typeof SpotColleagueHistory>;
export const SpotCommitteeHistory = z
  .object({
    /** The first award date counted and today, both in the organization's calendar. */
    from: z.iso.date(),
    to: z.iso.date(),
    items: z.array(SpotColleagueHistory),
  })
  .strict();
export type SpotCommitteeHistory = z.infer<typeof SpotCommitteeHistory>;
